<section class="ai-assistant-page">
  <input
    #attachmentInput
    type="file"
    class="hidden-input"
    [attr.accept]="acceptedAttachmentTypes"
    multiple
    (change)="onAttachmentSelected($event)"
    >

  <header class="page-header">
    <div>
      <p class="eyebrow">System</p>
      <h1>AI Assistant</h1>
      <p class="page-copy">
        Ask about QA data, logs, Jira tickets, Confluence runbooks, or global configuration.
      </p>
    </div>

    <div class="header-actions">
      <span class="environment-pill">
        <mat-icon>science</mat-icon>
        Environment: {{ environment | uppercase }}
      </span>
      <button mat-stroked-button type="button" (click)="startNewChat()" [disabled]="isBusy">
        <mat-icon>add_comment</mat-icon>
        New chat
      </button>
    </div>
  </header>

  <section class="thread-shell">
    @if (hasMessages) {
      <div class="thread">
        @for (message of messages; track trackByMessage($index, message)) {
          <article
            class="message"
            [class.user-message]="message.role === 'user'"
            [class.assistant-message]="message.role === 'assistant'"
            [class.error-message]="message.isError"
            >
            <div class="message-meta">
              <span class="message-role">{{ message.role === 'assistant' ? 'AI Assistant' : 'You' }}</span>
              @if (message.createdAt) {
                <span class="message-time">{{ message.createdAt | date : 'short' }}</span>
              }
            </div>
            @if (message.content) {
              @if (message.role === 'assistant') {
                <div class="message-body markdown-body" [innerHTML]="message.renderedContent"></div>
              } @else {
                <div class="message-body">{{ message.content }}</div>
              }
            }
            @if (message.attachments.length) {
              <section class="message-section attachment-section">
                <p class="section-label">{{ message.role === 'user' ? 'Attached files' : 'Attachments' }}</p>
                <div class="attachment-list">
                  @for (attachment of message.attachments; track attachment) {
                    <div class="attachment-chip attachment-display">
                      <mat-icon>{{ getAttachmentIcon(attachment) }}</mat-icon>
                      <div class="attachment-copy">
                        <span class="attachment-name">{{ getAttachmentLabel(attachment) }}</span>
                        <span class="attachment-meta">{{ getAttachmentSubtitle(attachment) }}</span>
                      </div>
                    </div>
                  }
                </div>
              </section>
            }
            @if (message.role === 'assistant' && message.toolCalls.length) {
              <section class="message-section">
                <p class="section-label">Looked at</p>
                <div class="tool-call-list">
                  @for (toolCall of message.toolCalls; track toolCall) {
                    <div class="tool-call">
                      <span class="tool-name">{{ toolCall.tool || 'Source' }}</span>
                      <span class="tool-summary">{{ toolCall.summary || 'Consulted during this answer.' }}</span>
                    </div>
                  }
                </div>
              </section>
            }
            @if (message.role === 'assistant' && message.citations.length) {
              <section class="message-section">
                <p class="section-label">Sources consulted</p>
                <div class="citation-groups">
                  @for (group of groupCitations(message.citations); track group.type) {
                    <div class="citation-group">
                      <span class="citation-type">{{ formatSourceType(group.type) }}</span>
                      <div class="citation-items">
                        @for (citation of group.items; track citation) {
                          @if (getCitationHref(citation)) {
                            <a
                              class="citation-chip"
                              [href]="getCitationHref(citation)"
                              target="_blank"
                              rel="noopener noreferrer"
                              >
                              {{ getCitationLabel(citation) }}
                            </a>
                          } @else {
                            <span class="citation-chip static">{{ getCitationLabel(citation) }}</span>
                          }
                        }
                      </div>
                    </div>
                  }
                </div>
              </section>
            }
          </article>
        }
        @if (sending) {
          <div class="loading-row">
            <mat-spinner [diameter]="20"></mat-spinner>
            <span>Waiting for the assistant...</span>
          </div>
        }
      </div>
    } @else {
      <div class="empty-state">
        <div class="empty-state-card">
          <mat-icon>smart_toy</mat-icon>
          <h2>Start a Team Admin investigation</h2>
          <p>
            The assistant can summarize QA issues without dumping raw secrets, SQL, or full log payloads by default.
          </p>
        </div>
        <div class="starter-grid">
          @for (starter of promptStarters; track starter) {
            <button
              mat-stroked-button
              type="button"
              class="starter-button"
              [disabled]="isBusy"
              (click)="usePromptStarter(starter)"
              >
              {{ starter }}
            </button>
          }
        </div>
        @if (restoring) {
          <div class="loading-row">
            <mat-spinner [diameter]="20"></mat-spinner>
            <span>Restoring your last conversation...</span>
          </div>
        }
      </div>
    }

  </section>

  <section class="composer-shell">
    @if (pendingAttachments.length) {
      <div class="pending-attachments">
        @for (attachment of pendingAttachments; track trackByPendingAttachment($index, attachment)) {
          <div class="attachment-chip">
            <mat-icon>{{ getAttachmentIcon(attachment) }}</mat-icon>
            <div class="attachment-copy">
              <span class="attachment-name">{{ getAttachmentLabel(attachment) }}</span>
              <span class="attachment-meta">{{ getAttachmentSubtitle(attachment) }}</span>
            </div>
            <button
              mat-icon-button
              type="button"
              aria-label="Remove attachment"
              (click)="removePendingAttachment(attachment.id)"
              >
              <mat-icon>close</mat-icon>
            </button>
          </div>
        }
      </div>
    }

    <mat-form-field appearance="outline" class="composer-field">
      <mat-label>Ask a question</mat-label>
      <textarea
        matInput
        rows="4"
        [(ngModel)]="draftMessage"
        [disabled]="isBusy"
        (keydown)="handleComposerKeydown($event)"
        placeholder="Why are UKG onboarding requests failing for Acme?"
      ></textarea>
      <mat-hint align="start">Press Enter to send. Shift+Enter adds a line break. Text is optional when files are attached.</mat-hint>
    </mat-form-field>

    <div class="composer-actions">
      <button mat-stroked-button type="button" (click)="attachmentInput.click()" [disabled]="isBusy">
        <mat-icon>attach_file</mat-icon>
        Attach files
      </button>
      <button mat-flat-button color="primary" type="button" (click)="sendMessage()" [disabled]="sendDisabled">
        <mat-icon>send</mat-icon>
        Send
      </button>
    </div>
  </section>
</section>
